import React, { useEffect } from "react";
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend
} from "chart.js";
import { Pie } from "react-chartjs-2";
import { useQuery } from "@tanstack/react-query";
import { GetFreePaidUserStats } from "../../../Api/subscription";
import { ErrorToast } from "../../../Utils/SweetAlert";

ChartJS.register(ArcElement, Tooltip, Legend);

const FreePaidChartTab = ({ onLoaded }) => {
    const { data: statsData, isFetching: isLoading } = useQuery({
        queryKey: ["get-free-paid-stats"],
        queryFn: () => GetFreePaidUserStats(),
        throwOnError: (error) => {
            ErrorToast(error?.response?.data?.message || "Server Error.");
        },
    });

    const stats = statsData?.data || [];
    const total = stats.reduce((acc, curr) => acc + curr.value, 0);

    const getPercentage = (value) => {
        if (total === 0) return 0;
        return ((value / total) * 100).toFixed(1);
    };

    const chartData = {
        labels: stats.map(item => `${item.label.charAt(0).toUpperCase() + item.label.slice(1)}`),
        datasets: [
            {
                label: "User Count",
                data: stats.map(item => item.value),
                backgroundColor: [
                    "#E8DBF3", // color for free
                    "#AD5ADC", // color for paid
                ],
                borderColor: [
                    "#D2B9EA",
                    "#8E44AD",
                ],
                borderWidth: 1,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    font: {
                        size: 14
                    },
                    padding: 20
                }
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const label = context.label || '';
                        const value = context.parsed;
                        const percentage = getPercentage(value);
                        return `${label}: ${value} users (${percentage}%)`;
                    }
                }
            }
        },
    };

    useEffect(() => {
        onLoaded && onLoaded({ total: 0, totals: null });
    }, [onLoaded]);

    return (
        <div className="card flex-fill border-card mb-3">
            <div className="card-header dashboard-chat-heading p-2">
                <h5 className="card-title text-black fw-bold fs-4 pl-2">Free vs Paid Users Distribution</h5>
            </div>
            <div className="card-body" style={{ minHeight: '60vh', padding: '20px' }}>
                {isLoading ? (
                    <div className="d-flex justify-content-center align-items-center h-100 py-5">
                        <div className="spinner-border loader-theme" role="status" />
                    </div>
                ) : (
                    <div className="row align-items-center h-100">
                        <div className="col-md-7">
                            <div className="d-flex justify-content-center">
                                <div style={{ width: '100%', maxWidth: '400px', height: '400px' }}>
                                    <Pie data={chartData} options={options} />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-5">
                            <div className="subscription-stats-summary p-4 rounded-4 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
                                <h6 className="fw-bold mb-4 text-uppercase text-secondary" style={{ letterSpacing: '1px' }}>User Breakdown</h6>
                                {stats.map((item, index) => (
                                    <div key={index} className="d-flex justify-content-between align-items-center mb-3 p-3 rounded-3 bg-white border">
                                        <div className="d-flex align-items-center">
                                            <div
                                                className="rounded-circle me-3"
                                                style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    backgroundColor: index === 0 ? "#E8DBF3" : "#AD5ADC"
                                                }}
                                            />
                                            <span className="text-capitalize fw-semibold">{item.label} Users</span>
                                        </div>
                                        <div className="text-end">
                                            <div className="fw-bold h5 mb-0">{item.value}</div>
                                            <small className="text-muted">{getPercentage(item.value)}% of total</small>
                                        </div>
                                    </div>
                                ))}
                                <div className="mt-4 pt-3 border-top d-flex justify-content-between align-items-center">
                                    <span className="fw-bold text-secondary">Total Users</span>
                                    <span className="fw-bold h4 mb-0" style={{ color: '#AD5ADC' }}>{total === 0 ? "" : total}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FreePaidChartTab;
